import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { isMarkdownRenderArtifact } from "@/services/multi-agent/artifactDisplay";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";

/**
 * A document referenced by an `artifact` part, fetched by its explicit id.
 *
 * NEVER BY LISTING. `RunState.artifacts[]` is ALWAYS EMPTY — no artifact type has a
 * `gui_protocol/mappers` registrant (handoff gotcha 2) — so a viewer that enumerated it would show
 * nothing and look broken. The `Part.ref` is the only handle that works, which is why this takes an
 * id and not a list.
 *
 * `fetchArtifact` is injected so the view is testable without the transport; the workspace passes
 * `getArtifact`.
 */
export interface ArtifactSectionProps {
  artifactId: string | null;
  fetchArtifact: (id: string) => Promise<{
    name?: string | null;
    content?: string | null;
    /** Weave v1 `Artifact.type` (also carried by the legacy REST `ArtifactContent.type`) —
     *  `plan_doc` / `report` / `markdown` route through a GFM renderer; see `isMarkdownRenderArtifact`. */
    type?: string | null;
    /** Legacy REST `ArtifactContent.language` — `"markdown"` is the other markdown signal on that path. */
    language?: string | null;
    /** Weave v1 `Artifact.render` (`diff|code|table|plan|markdown|download`) — the most direct signal
     *  when the fetcher carries it (the legacy REST endpoint does not). */
    render?: string | null;
  }>;
}

export function ArtifactSection({ artifactId, fetchArtifact }: ArtifactSectionProps) {
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "ready"; name: string; content: string; isMarkdown: boolean }
    | { kind: "error" }
  >({ kind: "idle" });

  useEffect(() => {
    if (!artifactId) {
      setState({ kind: "idle" });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    void (async () => {
      try {
        const artifact = await fetchArtifact(artifactId);
        // The id can change while a fetch is open (a second card clicked). Without this the first
        // response would overwrite the second document.
        if (cancelled) return;
        setState({
          kind: "ready",
          name: artifact.name || "Document",
          content: artifact.content ?? "",
          isMarkdown: isMarkdownRenderArtifact(artifact),
        });
      } catch {
        if (!cancelled) setState({ kind: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [artifactId, fetchArtifact]);

  if (!artifactId) {
    return <p className="px-1 py-6 text-center text-[13px] text-muted-foreground">No document selected.</p>;
  }
  if (state.kind === "loading" || state.kind === "idle") {
    return (
      <p role="status" className="flex items-center justify-center gap-2 py-8 text-[13px] text-muted-foreground">
        <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
        Opening…
      </p>
    );
  }
  if (state.kind === "error") {
    return (
      // Fixed copy: the artifact endpoint's 404 body is not worth relaying (§10), and the run's own
      // surfaces already carry the same content structurally.
      <p role="alert" className="px-1 py-6 text-center text-[13px] text-muted-foreground">
        Couldn&rsquo;t open this document. The details above cover the same content.
      </p>
    );
  }

  return <ReadyArtifactView name={state.name} content={state.content} isMarkdown={state.isMarkdown} />;
}

/**
 * The "ready" render — split out from `ArtifactSection` (which owns the fetch/state machine) so
 * it's directly unit-testable via `renderToStaticMarkup` without a DOM harness or a way to await
 * an effect (this repo has neither — see `PendingInteractionCard.test.tsx`'s own note on the same
 * gap). Same pattern `ArtifactCard.tsx` uses for `ChangePlanView`/`CodePatchFixView`.
 *
 * §2.18 requires every `render: markdown` artifact (plan_doc / report / markdown — canonical GFM
 * from the backend: LF, no trailing whitespace, max one blank line, single trailing newline) to
 * render through a GFM markdown renderer, never `white-space: pre`/`pre-wrap` and never
 * `remark-breaks`. `SkillResultMarkdown` (Streamdown + `defaultRemarkPlugins`, which includes
 * remark-gfm) is the SAME renderer the legacy overlay path (`ArtifactCard.tsx` ->
 * `ArtifactOverlay.tsx`) uses, so a plan/report reads identically (headings, lists, tables)
 * whether opened from the legacy card or this workspace viewer. `markdown-artifact-body`
 * (`streamdownOverrides.css`) gives both that compact, "Claude-artifact-like" paragraph/list/
 * heading spacing — Streamdown's own `<p>` carries no margin at all, and Tailwind Preflight zeroes
 * the UA default, so without it paragraphs would render with NO vertical gap.
 * Everything else (code/diff/table renders, and anything not markdown-typed) keeps the raw `<pre>`
 * dump — a second rich rendering of an editable artefact (a code/diff render) would invite the
 * reader to treat this preview as the thing to edit.
 */
export function ReadyArtifactView({
  name,
  content,
  isMarkdown,
}: {
  name: string;
  content: string;
  isMarkdown: boolean;
}) {
  return (
    <section aria-label={name} className="rounded-md border border-border bg-card">
      <header className="border-b border-border px-3 py-2">
        <h3 className="font-mono text-[12px] text-foreground">{name}</h3>
      </header>
      {isMarkdown ? (
        <div className="markdown-artifact-body max-h-[70vh] overflow-auto px-3 py-2">
          <SkillResultMarkdown content={content} isStreaming={false} className="text-foreground text-[13px]" />
        </div>
      ) : (
        <pre className="max-h-[70vh] overflow-auto px-3 py-2 font-mono text-[12px] leading-relaxed text-muted-foreground">
          {content}
        </pre>
      )}
    </section>
  );
}
